(function () {
  const SLOT_CLASSES = [
    'collection-list-card--slot-hero',
    'collection-list-card--slot-wide',
    'collection-list-card--slot-compact'
  ];

  function normalizeDeepLinkedPage() {
    const url = new URL(window.location.href);
    const rawPage = url.searchParams.get('page');
    if (!rawPage) return false;

    const pageNum = Number(rawPage);
    if (!Number.isFinite(pageNum) || pageNum <= 1) return false;

    url.searchParams.delete('page');
    const nextUrl = `${url.pathname}${url.searchParams.toString() ? `?${url.searchParams.toString()}` : ''}${url.hash}`;
    window.location.replace(nextUrl);
    return true;
  }

  function initCollectionsPage() {
    const page = document.querySelector('[data-collections-page]');
    if (!page || page.dataset.collectionsReady === 'true') return;

    // Locked behavior: deep links to page>1 normalize to page 1 feed.
    if (normalizeDeepLinkedPage()) return;

    const grid = page.querySelector('[data-collections-grid]');
    if (!grid) return;

    page.dataset.collectionsReady = 'true';

    const searchInput = page.querySelector('[data-collections-search]');
    const clearButton = page.querySelector('[data-collections-search-clear]');
    const searchWrap = page.querySelector('[data-collections-search-wrap]');
    const searchStatus = page.querySelector('[data-collections-search-status]');
    const orderSelect = page.querySelector('[data-collections-order]');
    const visibleCount = page.querySelector('[data-collection-visible-count]');
    const countLabel = page.querySelector('[data-collection-count-label]');
    const emptyState = page.querySelector('[data-collections-empty]');
    const progressLabel = page.querySelector('[data-collections-progress]');

    const getCards = () => Array.from(grid.querySelectorAll('[data-collection-card]'));
    let isReordering = false;
    let refreshQueued = false;
    let queuedDetail = null;
    let fallbackObserver;

    const statusPriority = (status) => {
      if (status === 'ended') return 3;
      if (status === 'appended') return 2;
      if (status === 'loading') return 1;
      return 0;
    };

    const mergeRefreshDetail = (current = {}, incoming = {}) => {
      if (!current || Object.keys(current).length === 0) return { ...incoming };
      if (!incoming || Object.keys(incoming).length === 0) return { ...current };

      const merged = { ...current, ...incoming };
      const currentStatus = current.status;
      const incomingStatus = incoming.status;

      if (currentStatus && incomingStatus) {
        merged.status = statusPriority(incomingStatus) >= statusPriority(currentStatus)
          ? incomingStatus
          : currentStatus;
      } else if (currentStatus && !incomingStatus) {
        merged.status = currentStatus;
      }

      return merged;
    };

    const updateVisibleCount = (count) => {
      if (visibleCount) visibleCount.textContent = String(count);
      if (countLabel) countLabel.textContent = count === 1 ? 'collection' : 'collections';
    };

    const updateSearchStatus = (term, visible, total) => {
      if (!searchStatus) return;

      if (!term) {
        searchStatus.textContent = 'Find your pathway';
        return;
      }

      searchStatus.textContent = `Showing ${visible} of ${total} collections for "${term}"`;
    };

    const updateProgress = (detail = {}) => {
      if (!progressLabel) return;

      const loadedCount = getCards().length;
      progressLabel.dataset.loadedCount = String(loadedCount);

      const trigger = page.querySelector('[data-infinite-scroll-trigger]');
      const isEnded = detail.status === 'ended' || !trigger;

      if (isEnded) {
        progressLabel.textContent = 'All collections loaded';
        return;
      }

      if (detail.status === 'loading') {
        progressLabel.textContent = `${loadedCount} ${loadedCount === 1 ? 'collection' : 'collections'} loaded · loading more...`;
        return;
      }

      progressLabel.textContent = `${loadedCount} ${loadedCount === 1 ? 'collection' : 'collections'} loaded`;
    };

    const assignMosaicSlots = () => {
      const cards = getCards();
      cards.forEach((card) => card.classList.remove(...SLOT_CLASSES));

      const visibleCards = cards.filter((card) => !card.hidden);
      visibleCards.forEach((card, index) => {
        const slot = index % 7;
        if (slot === 0) {
          card.classList.add('collection-list-card--slot-hero');
        } else if (slot === 3) {
          card.classList.add('collection-list-card--slot-wide');
        } else if (slot === 4) {
          card.classList.add('collection-list-card--slot-compact');
        }
      });
    };

    const revealCards = () => {
      getCards().forEach((card) => {
        if (card.classList.contains('reveal') && !card.classList.contains('is-visible')) {
          card.classList.add('is-visible');
          // After animation completes, pin visible state and strip the reveal
          // class so future DOM moves by sortCards don't restart the animation
          card.addEventListener('animationend', () => {
            card.style.opacity = '1';
            card.style.transform = '';
            card.classList.remove('reveal');
            card.classList.remove('is-visible');
          }, { once: true });
        }
      });
    };

    const sortCards = () => {
      const cards = getCards();
      if (!cards.length) return;

      const order = orderSelect ? orderSelect.value : 'featured';
      const sortedCards = cards.slice().sort((a, b) => {
        const titleA = a.dataset.collectionTitle || '';
        const titleB = b.dataset.collectionTitle || '';
        const countA = Number(a.dataset.collectionCount || 0);
        const countB = Number(b.dataset.collectionCount || 0);
        const indexA = Number(a.dataset.collectionIndex || 0);
        const indexB = Number(b.dataset.collectionIndex || 0);

        if (order === 'featured') return indexA - indexB;
        if (order === 'products_desc') {
          if (countB !== countA) return countB - countA;
          return titleA.localeCompare(titleB, undefined, { sensitivity: 'base' });
        }
        if (order === 'title_asc') {
          return titleA.localeCompare(titleB, undefined, { sensitivity: 'base' });
        }

        return indexA - indexB;
      });

      // Only manipulate DOM if the order actually changed
      const needsReorder = sortedCards.some((card, i) => card !== cards[i]);
      if (!needsReorder) return;

      isReordering = true;
      const fragment = document.createDocumentFragment();
      sortedCards.forEach((card) => fragment.appendChild(card));
      grid.appendChild(fragment);
      isReordering = false;
    };

    const applyFilter = () => {
      const term = searchInput ? searchInput.value.trim().toLowerCase() : '';
      const cards = getCards();
      const total = cards.length;
      let visible = 0;

      cards.forEach((card) => {
        const keywords = card.dataset.collectionKeywords || '';
        const isMatch = !term || keywords.includes(term);
        card.hidden = !isMatch;
        if (isMatch) visible += 1;
      });

      updateVisibleCount(visible);
      assignMosaicSlots();
      updateSearchStatus(term, visible, total);

      if (clearButton) {
        clearButton.hidden = term.length === 0;
      }
      if (searchWrap) {
        searchWrap.classList.toggle('is-active', term.length > 0);
      }

      if (emptyState) {
        emptyState.hidden = visible !== 0;
      }
    };

    const refreshCollectionView = (detail = {}) => {
      sortCards();
      // Force reflow after potential DOM moves so the browser paints the
      // pre-animation state before we trigger reveal-up via is-visible
      void grid.offsetHeight;
      revealCards();
      applyFilter();
      updateProgress(detail);
    };

    const queueRefresh = (detail = {}) => {
      queuedDetail = mergeRefreshDetail(queuedDetail || {}, detail || {});
      if (refreshQueued) return;
      refreshQueued = true;
      requestAnimationFrame(() => {
        refreshQueued = false;
        const detailForRefresh = queuedDetail || {};
        queuedDetail = null;
        refreshCollectionView(detailForRefresh);
      });
    };

    if (searchInput) {
      searchInput.addEventListener('input', () => {
        applyFilter();
        updateProgress();
      });

      searchInput.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && searchInput.value.length > 0) {
          searchInput.value = '';
          applyFilter();
          updateProgress();
          searchInput.blur();
        }
      });
    }

    if (searchInput && !window.collectionsSearchShortcutBound) {
      document.addEventListener('keydown', (event) => {
        const target = event.target;
        const isTextInput = target && (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable
        );
        if (event.key !== '/' || isTextInput || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
          return;
        }
        event.preventDefault();
        searchInput.focus();
      });
      window.collectionsSearchShortcutBound = true;
    }

    if (clearButton && searchInput) {
      clearButton.addEventListener('click', () => {
        searchInput.value = '';
        searchInput.focus();
        applyFilter();
        updateProgress();
      });
    }

    if (orderSelect) {
      orderSelect.addEventListener('change', () => queueRefresh());
    }

    document.addEventListener('infinite-scroll:update', (event) => {
      const detail = event.detail || {};
      if (detail.grid && detail.grid !== grid) return;
      if (detail.status === 'error') return;
      queueRefresh(detail);
    });

    fallbackObserver = new MutationObserver((mutations) => {
      if (isReordering) return;
      const hasAddedCards = mutations.some((mutation) => {
        if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) return false;
        return Array.from(mutation.addedNodes).some((node) => {
          if (!(node instanceof Element)) return false;
          return node.matches('[data-collection-card]') || node.querySelector('[data-collection-card]');
        });
      });
      if (hasAddedCards) {
        queueRefresh({ status: 'appended' });
      }
    });
    fallbackObserver.observe(grid, { childList: true });

    queueRefresh();
  }

  document.addEventListener('DOMContentLoaded', initCollectionsPage);
  document.addEventListener('shopify:section:load', initCollectionsPage);
})();
